import { useTranslation } from "react-i18next";
import { useNavigationState } from "@/zustand/NavigationState";
import { ChevronLeft } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState, useRef, useEffect, useMemo } from "react";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { useToast } from "@/hooks/useToast";
import {
  prepareGroupChatOwner,
  prepareMemberForNewGroupChat,
} from "@/middleware/members";
import { createNewAIChat } from "@/middleware/chats";
import { CHAT_HUB_ID } from "@/config/constants";
import { MemberRole } from "@/server/types/member";
import {
  postNewMessageInChat,
  postNewMessageWithFileAttachmentToChat,
  postNewMessageWithMediaAttachmentToChat,
} from "@/middleware/messages";
import { cn } from "@/lib/utils";
import { Button } from "@/components/primitives/button";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { AIChatFormSchema, AIChatFormType } from "@/schemas/chat";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { attachmentSizeRejection } from "@/utils/attachmentSizeLimit";
import { computeMessageLimitState } from "@/lib/messageLimits";
import { toPlainText } from "@/lib/markdown";
import {
  generateConversationTitle,
  generateDateFallbackTitle,
} from "@/utils/conversationTitle";
import { useNewAIChatVoice } from "./NewAIChatForm/hooks/useNewAIChatVoice";
import PlanLimitUpsell from "@/components/shared/PlanLimitUpsell";
import LoadingScreen from "@/components/shared/LoadingScreen";
import NewAIChatInput from "./NewAIChatForm/NewAIChatInput";

export default function NewAIChatForm() {
  const { toast } = useToast();
  const { t } = useTranslation();
  const setChatUserAction = useNavigationState(
    (state) => state.setChatUserAction,
  );

  const initNewChat = useNewGroupChatState((state) => state.initNewChat);
  const cancelAIChatCreation = useNavigationState(
    (state) => state.cancelAIChatCreation,
  );

  const { newFileAttachment, newFileAttachmentId, discardFileAttachment } =
    useFileMessageSenderState();

  const {
    newMediaAttachment,
    newMediaAttachmentId,
    newMediaThumbnailId,
    thumbnailBlob,
    discardMediaAttachment,
  } = useMediaMessageSenderState();

  const [isMultiLine, setIsMultiLine] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const hasFileAttachment = !!(newFileAttachment && newFileAttachmentId);
  const hasMediaAttachment = !!(
    newMediaAttachment &&
    newMediaAttachmentId &&
    newMediaThumbnailId &&
    thumbnailBlob
  );
  const hasAttachment = hasFileAttachment || hasMediaAttachment;

  const handleTextareaResize = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;

    el.style.height = "auto";
    const scrollH = el.scrollHeight;
    const next = scrollH > el.clientHeight;
    setIsMultiLine((prev) => (prev === next ? prev : next));
    el.style.height = `${scrollH}px`;
  };

  const form = useForm<AIChatFormType>({
    resolver: zodResolver(AIChatFormSchema),
    defaultValues: {
      prompt: "",
    },
  });

  const watchedValue = form.watch("prompt");

  const messageLimits = useMemo(
    () =>
      computeMessageLimitState({
        plainText: toPlainText(watchedValue ?? ""),
        html: watchedValue ?? "",
      }),
    [watchedValue],
  );

  const showCounter =
    messageLimits.showCloseToLimitWarning ||
    messageLimits.isOverCharLimit ||
    messageLimits.isOverEncryptedLimit;

  const isOverLimit =
    messageLimits.isOverCharLimit || messageLimits.isOverEncryptedLimit;

  const resetForm = () => {
    form.reset();
    setIsMultiLine(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  useEffect(() => {
    if (
      !form.formState.isSubmitting &&
      (!watchedValue || watchedValue.trim() === "")
    ) {
      setIsMultiLine(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  }, [watchedValue, form.formState.isSubmitting]);

  // Discard attachment state when navigating away
  useEffect(() => {
    return () => {
      discardFileAttachment();
      discardMediaAttachment();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isRecording,
    isSending,
    duration,
    isNearMaxDuration,
    liveAmplitude,
    isLocked,
    slideProgress,
    lockProgress,
    showVoiceUpsell,
    nextPlan,
    requestMic,
    handleStartRecording,
    handleGestureStart,
    handleLockedSend,
    handleLockedCancel,
    handleImmediateSend,
    handleImmediateLock,
    voiceRecordingStatus,
  } = useNewAIChatVoice();

  const isCreating = form.formState.isSubmitting || isSending;

  const canSubmit =
    (messageLimits.charCount > 0 || hasAttachment) &&
    !isOverLimit &&
    !isCreating;

  const canSendMessage = messageLimits.charCount > 0 || hasAttachment;

  const handleCancel = () => {
    discardFileAttachment();
    discardMediaAttachment();
    cancelAIChatCreation();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (IS_MOBILE || isCreating) {
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.handleSubmit(onSubmit)();
    }
  };

  async function onSubmit(values: AIChatFormType) {
    if (
      !toPlainText(values.prompt).trim() &&
      !hasFileAttachment &&
      !hasMediaAttachment
    ) {
      return;
    }
    if (isOverLimit) {
      return;
    }

    // Size-check the staged attachment HERE, before the chat is created.
    //
    // The reason this form needs its own check: the sender stores are
    // module-global, and `ChatPlayground` UNMOUNTS when this form takes over,
    // so nothing about the previous composer's state is guaranteed to have been
    // cleared by the swap itself. `useRtfState`'s discard effect now has an
    // unmount cleanup that closes that path, but this check does not depend on
    // it — a file that arrives here staged was measured against some other
    // chat's flat cap, and only a check on this side knows the Architect one.
    //
    // The send-time check in `messages.ts` also catches it, but only after
    // `createNewAIChat` has written the chat locally and on the server and the
    // attachment has been discarded — leaving an orphan chat, no file and no
    // prompt. Rejecting first costs nothing and keeps all three.
    //
    // `isArchitect: true` is not inferred: this form only renders under
    // CREATE_AI_CHAT, so the destination is always an Architect chat.
    //
    // Pick the attachment the SEND will actually use, mirroring the branch
    // order below (media only when fully sendable, else the file). A plain
    // `media ?? file` would size-check a half-staged media while the send
    // fell through to a PDF that skipped this gate — landing it in the
    // send-time check only AFTER the chat is created, the exact orphan-chat
    // outcome this gate exists to prevent.
    const stagedAttachment = hasMediaAttachment
      ? newMediaAttachment
      : hasFileAttachment
        ? newFileAttachment
        : null;
    if (stagedAttachment) {
      const sizeRejection = attachmentSizeRejection(stagedAttachment.size, {
        fileType: stagedAttachment.type,
        isArchitect: true,
        plan: useSubscriptionState.getState().subscription.plan,
      });
      if (sizeRejection) {
        toast({
          duration: 1500,
          description: sizeRejection,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      initNewChat();
      await prepareGroupChatOwner();
      await prepareMemberForNewGroupChat(CHAT_HUB_ID);

      const titleFallback = generateDateFallbackTitle();

      const chatId = await createNewAIChat({
        title: generateConversationTitle(values.prompt, 30, titleFallback),
        avatar: "",
        isSearchable: false,
        description: "",
        allowThreads: false,
        canPost: MemberRole.MEMBER,
      });

      // Capture attachment state before discarding
      const fileToSend = newFileAttachment;
      const fileId = newFileAttachmentId;
      const mediaToSend = newMediaAttachment;
      const mediaId = newMediaAttachmentId;
      const thumbnailId = newMediaThumbnailId;
      const thumbnail = thumbnailBlob;

      discardFileAttachment();
      discardMediaAttachment();

      if (hasMediaAttachment && mediaToSend && thumbnail) {
        await postNewMessageWithMediaAttachmentToChat({
          chatId,
          file: mediaToSend,
          newAttachmentId: mediaId,
          newThumbnailId: thumbnailId,
          thumbnailBlob: thumbnail,
          content: values.prompt,
          // This send is awaited behind the full-screen "creating conversation"
          // loader — queueing it would freeze the user; and a brand-new chat's
          // first message has nothing to overlap with. Bypass the send cap.
          skipSendQueue: true,
        });
      } else if (hasFileAttachment && fileToSend) {
        await postNewMessageWithFileAttachmentToChat({
          chatId,
          file: fileToSend,
          newAttachmentId: fileId,
          content: values.prompt,
          skipSendQueue: true,
        });
      } else {
        await postNewMessageInChat({
          chatId,
          content: values.prompt,
        });
      }

      setChatUserAction(null);
      resetForm();
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  return (
    <div className="absolute inset-0 flex size-full flex-col overflow-hidden bg-card lg:border-l lg:border-border">
      {isCreating && (
        <LoadingScreen
          className="z-10 bg-card"
          title={t("creatingConversation")}
        />
      )}
      <div className="pointer-events-none absolute left-1/2 top-[10%] h-[30rem] w-full -translate-x-1/2 bg-[radial-gradient(circle_at_center,_hsl(var(--gradient-color)/0.6),_transparent_70%)] opacity-60 blur-3xl lg:-top-28 lg:h-96 lg:w-1/2" />
      <div className="architect-glow-grain pointer-events-none absolute left-1/2 top-[10%] h-[30rem] w-full -translate-x-1/2 lg:-top-28 lg:h-96 lg:w-1/2" />

      <div
        className={cn(
          "flex h-middleColumnHeaderHeight items-center px-4 align-middle lg:hidden lg:px-6",
        )}
      >
        <Button
          className="mr-2 text-secondary"
          size="icon"
          variant="ghost"
          onClick={handleCancel}
        >
          <ChevronLeft />
        </Button>
      </div>

      <div className="flex size-full flex-col items-center justify-center space-y-2 px-5 align-middle lg:mx-auto lg:max-w-[1000px]">
        <div className="py-7">
          <img
            src="/logo/architect_light_logo.png"
            alt="The Architect"
            className="w-80 invert dark:invert-0"
          />
        </div>

        {showVoiceUpsell && (
          <PlanLimitUpsell
            message={t("subscription.voiceNoteLimitReached")}
            targetPlan={nextPlan}
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full">
            <FormField
              control={form.control}
              disabled={isCreating}
              name="prompt"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <NewAIChatInput
                      field={field}
                      textareaRef={textareaRef}
                      isMultiLine={isMultiLine}
                      hasAttachment={hasAttachment}
                      handleKeyDown={handleKeyDown}
                      handleTextareaResize={handleTextareaResize}
                      isRecording={isRecording}
                      isLocked={isLocked}
                      duration={duration}
                      isNearMaxDuration={isNearMaxDuration}
                      liveAmplitude={liveAmplitude}
                      slideProgress={slideProgress}
                      lockProgress={lockProgress}
                      onLockedSend={handleLockedSend}
                      onLockedCancel={handleLockedCancel}
                      canSendMessage={canSendMessage}
                      canSubmit={canSubmit}
                      isSubmitting={form.formState.isSubmitting}
                      voiceRecordingStatus={voiceRecordingStatus}
                      requestMic={requestMic}
                      onStartRecording={handleStartRecording}
                      onGestureStart={handleGestureStart}
                      onImmediateSend={handleImmediateSend}
                      onImmediateLock={handleImmediateLock}
                      showCounter={showCounter}
                      isOverLimit={isOverLimit}
                      messageLimits={messageLimits}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
    </div>
  );
}
